import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useLocales } from 'src/locales';
import Iconify from 'src/components/iconify';
import ConfirmDialog from 'src/components/confirm-dialog';

import { EntityCostFee, EntityCostFeeDTO } from '../../../@types/vehicleCostFee';
import {
  useCreateEntityCostFeeMutation,
  useDeleteEntityCostFeeMutation,
  useGetEntityCostFeesByTourBundleQuery,
  useUpdateEntityCostFeeMutation,
} from '../../../redux/slices/entityCostFeeSlice';

interface Props {
  tourBundleId: string;
}

export default function EntityCostFeeManagement({ tourBundleId }: Props) {
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const tourBundleIdNum = useMemo(() => Number(tourBundleId), [tourBundleId]);
  const [openEntityDialog, setOpenEntityDialog] = useState(false);
  const [editingEntityFee, setEditingEntityFee] = useState<EntityCostFee | null>(null);
  const [entityFeeToDelete, setEntityFeeToDelete] = useState<EntityCostFee | null>(null);
  const [entityFeeFormData, setEntityFeeFormData] = useState<Partial<EntityCostFeeDTO>>({
    entityType: 'VEHICLE',
    costFee: 0,
  });

  const { data: entityFees, isLoading: isLoadingEntityFees } =
    useGetEntityCostFeesByTourBundleQuery(tourBundleId);

  const [createEntityFee] = useCreateEntityCostFeeMutation();
  const [updateEntityFee] = useUpdateEntityCostFeeMutation();
  const [deleteEntityFee, { isLoading: isDeletingEntityFee }] = useDeleteEntityCostFeeMutation();

  const entityFeesList = entityFees ?? [];

  const handleOpenEntityDialog = (fee?: EntityCostFee) => {
    if (fee) {
      setEditingEntityFee(fee);
      setEntityFeeFormData({ ...fee });
    } else {
      setEditingEntityFee(null);
      setEntityFeeFormData({
        entityType: 'VEHICLE',
        costFee: 0,
      });
    }
    setOpenEntityDialog(true);
  };

  const handleCloseEntityDialog = () => {
    setOpenEntityDialog(false);
    setEditingEntityFee(null);
  };

  const handleDeleteEntityFee = async () => {
    if (!entityFeeToDelete) {
      return;
    }

    try {
      await deleteEntityFee(entityFeeToDelete.id).unwrap();
      setEntityFeeToDelete(null);
      enqueueSnackbar(`${translate('vehicleCostFees.entityFee.deleteSuccess')}`, {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar(`${translate('vehicleCostFees.entityFee.deleteError')}`, {
        variant: 'error',
      });
    }
  };

  const handleSaveEntityFee = async () => {
    if (editingEntityFee) {
      try {
        const updatePayload = {
          id: editingEntityFee.id!,
          data: {
            tourBundleId: tourBundleIdNum,
            entityType: entityFeeFormData.entityType!,
            costFee: entityFeeFormData.costFee!,
          },
        };

        await updateEntityFee(updatePayload).unwrap();

        enqueueSnackbar(`${translate('vehicleCostFees.entityFee.updateSuccess')}`, {
          variant: 'success',
        });
        handleCloseEntityDialog();
      } catch {
        enqueueSnackbar(`${translate('vehicleCostFees.entityFee.updateError')}`, {
          variant: 'error',
        });
      }
    } else {
      try {
        const createPayload: EntityCostFeeDTO = {
          entityType: entityFeeFormData.entityType!,
          costFee: entityFeeFormData.costFee!,
          tourBundleId: tourBundleIdNum,
        };

        await createEntityFee(createPayload).unwrap();
        enqueueSnackbar(`${translate('vehicleCostFees.entityFee.createSuccess')}`, {
          variant: 'success',
        });
        handleCloseEntityDialog();
      } catch {
        enqueueSnackbar(`${translate('vehicleCostFees.entityFee.createError')}`, {
          variant: 'error',
        });
      }
    }
  };

  if (isLoadingEntityFees) {
    return (
      <Card sx={{ p: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h6">{`${translate('vehicleCostFees.entityFee.title')}`}</Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => handleOpenEntityDialog()}
            sx={{ textTransform: 'none' }}
            disabled={entityFeesList.length > 0}
          >
            {entityFeesList.length > 0
              ? `${translate('vehicleCostFees.entityFee.edit')}`
              : `${translate('vehicleCostFees.entityFee.new')}`}
          </Button>
        </Stack>
        {entityFeesList.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{`${translate('vehicleCostFees.entityFee.entityType')}`}</TableCell>
                  <TableCell align="center">{`${translate('vehicleCostFees.costFee')}`}</TableCell>
                  <TableCell align="center">{`${translate('actions')}`}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entityFeesList.map((fee) => (
                  <TableRow key={fee.id}>
                    <TableCell>
                      {`${translate(`vehicleCostFees.entityFee.types.${fee.entityType}`)}`}
                    </TableCell>
                    <TableCell align="center">{fee.costFee.toFixed(2)} €</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEntityDialog(fee)}
                        color="primary"
                      >
                        <Iconify icon="eva:edit-fill" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setEntityFeeToDelete(fee)}
                        color="error"
                      >
                        <Iconify icon="eva:trash-2-outline" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {`${translate('vehicleCostFees.entityFee.noData')}`}
            </Typography>
          </Box>
        )}
      </Card>

      <Dialog open={openEntityDialog} onClose={handleCloseEntityDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEntityFee
            ? `${translate('vehicleCostFees.entityFee.edit')}`
            : `${translate('vehicleCostFees.entityFee.new')}`}
        </DialogTitle>
        <DialogContent>
          <Grid2 container spacing={2} sx={{ mt: 1 }}>
            <Grid2 size={12}>
              <FormControl fullWidth>
                <InputLabel>{`${translate('vehicleCostFees.entityFee.entityType')}`}</InputLabel>
                <Select
                  value={entityFeeFormData.entityType || 'VEHICLE'}
                  label={`${translate('vehicleCostFees.entityFee.entityType')}`}
                  onChange={(e) =>
                    setEntityFeeFormData({
                      ...entityFeeFormData,
                      entityType: e.target.value as 'VEHICLE',
                    })
                  }
                  disabled={!!editingEntityFee}
                >
                  <MenuItem value="VEHICLE">
                    {`${translate('vehicleCostFees.entityFee.types.VEHICLE')}`}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid2>
            <Grid2 size={12}>
              <TextField
                fullWidth
                type="number"
                label={`${translate('vehicleCostFees.costFee')}`}
                value={entityFeeFormData.costFee || 0}
                onChange={(e) =>
                  setEntityFeeFormData({
                    ...entityFeeFormData,
                    costFee: parseFloat(e.target.value) || 0,
                  })
                }
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEntityDialog} sx={{ textTransform: 'none' }}>
            {`${translate('cancel')}`}
          </Button>
          <Button onClick={handleSaveEntityFee} variant="contained" sx={{ textTransform: 'none' }}>
            {`${translate('save')}`}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={entityFeeToDelete !== null}
        title={`${translate('delete')}`}
        content={`${translate('vehicleCostFees.entityFee.deleteConfirm')}`}
        action={
          <Button
            variant="contained"
            color="error"
            loading={isDeletingEntityFee}
            onClick={handleDeleteEntityFee}
          >
            {`${translate('delete')}`}
          </Button>
        }
        onClose={() => {
          if (!isDeletingEntityFee) {
            setEntityFeeToDelete(null);
          }
        }}
      />
    </>
  );
}
